import React, { useMemo, useState } from 'react';
import MapGL, { Source, Layer, NavigationControl, Marker } from 'react-map-gl';
import { cellsByScale, defaultScale, cellBbox, cellHeader, pmlColor } from './cellMapData';

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

export default function CellMap({ data }) {
  const scales = cellsByScale(data);
  const [active, setActive] = useState(() => defaultScale(data));
  const activeScale = scales.find((s) => s.scaleMi === active) || scales[0];
  const cells = useMemo(() => (activeScale ? activeScale.cells : []), [activeScale]);

  const initial = useMemo(() => {
    if (!cells.length) return { latitude: 39.5, longitude: -98.35, zoom: 3 };
    const [minLon, minLat, maxLon, maxLat] = cellBbox(cells);
    return { latitude: (minLat + maxLat) / 2, longitude: (minLon + maxLon) / 2, zoom: 9 };
  }, [cells]);
  const [vp, setVp] = useState(initial);
  React.useEffect(() => setVp(initial), [initial]);

  if (!scales.length) {
    return <div style={S.empty}>Gate-4 PML not yet available for this quote&apos;s locations.</div>;
  }

  const hexFc = {
    type: 'FeatureCollection',
    features: cells.map((c) => ({
      type: 'Feature',
      properties: { breached: !!c.breached },
      geometry: { type: 'Polygon', coordinates: [c.polygon] },
    })),
  };
  const maxRoof = Math.max(1, ...cells.flatMap((c) => (c.rooftops || []).map((r) => r.pml250Standalone || 0)));

  return (
    <div>
      <div style={S.tabs}>
        {scales.map((s) => (
          <button
            key={s.scaleMi}
            type="button"
            onClick={() => setActive(s.scaleMi)}
            style={{ ...S.tab, ...(s.scaleMi === active ? S.tabActive : {}) }}
          >
            {s.scaleMi} mi
          </button>
        ))}
      </div>
      {cells.map((c) => (
        <div key={c.cellId} style={{ ...S.header, color: c.breached ? '#991b1b' : '#637381' }}>
          {cellHeader(c)}
          {c.stale ? <span style={S.stale}> &middot; refreshing</span> : null}
        </div>
      ))}
      <div style={S.mapWrap}>
        <MapGL {...vp} width="100%" height={420} mapStyle="mapbox://styles/mapbox/light-v10" mapboxApiAccessToken={TOKEN} onViewportChange={setVp}>
          <NavigationControl style={{ right: 8, top: 8 }} />
          <Source id="hex" type="geojson" data={hexFc}>
            <Layer id="hex-fill" type="fill" paint={{ 'fill-color': '#5F2994', 'fill-opacity': 0.06 }} />
            <Layer
              id="hex-line"
              type="line"
              paint={{
                'line-color': ['case', ['get', 'breached'], '#5F2994', '#919EAB'],
                'line-width': 2,
              }}
            />
          </Source>
          {cells.flatMap((c) =>
            (c.rooftops || []).map((r) => (
              <Marker key={`roof-${c.cellId}-${r.locationId}`} latitude={r.lat} longitude={r.lon}>
                <span style={{ ...S.dot, background: pmlColor(r.pml250Standalone, maxRoof) }} title={r.name} />
              </Marker>
            ))
          )}
          {(data.quoteLocations || []).map((q) => (
            <Marker key={`q-${q.locationId}`} latitude={q.lat} longitude={q.lon}>
              <span style={S.quoteDot} title={`${q.name} (this quote)`} />
            </Marker>
          ))}
        </MapGL>
      </div>
    </div>
  );
}

const S = {
  tabs: { display: 'flex', gap: 6, margin: '4px 0 8px' },
  tab: {
    border: '1px solid #DFE3E8',
    background: '#fff',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: 12,
    cursor: 'pointer',
    color: '#637381',
  },
  tabActive: { background: '#5F2994', borderColor: '#5F2994', color: '#fff', fontWeight: 600 },
  header: { fontSize: 12, fontWeight: 600, margin: '2px 0' },
  stale: { color: '#8a5a00', fontWeight: 600 },
  mapWrap: { height: 420, borderRadius: 8, overflow: 'hidden', border: '1px solid #DFE3E8', marginTop: 6 },
  empty: { fontSize: 12, color: '#637381', padding: '8px 0' },
  dot: {
    display: 'block',
    width: 12,
    height: 12,
    borderRadius: '50%',
    border: '1.5px solid #fff',
    boxShadow: '0 0 0 1px #919EAB',
  },
  quoteDot: {
    display: 'block',
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: '#5F2994',
    border: '3px solid #fff',
    boxShadow: '0 0 0 2px #5F2994',
  },
};
